import { useEffect, useMemo, useRef, useState } from "react";
import { Archive, Loader2, Plus } from "lucide-react";
import { Button } from "@sphinx/design-system/button";
import { ScrollArea } from "@sphinx/design-system/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@sphinx/design-system/tooltip";
import { cn } from "@/lib/utils";
import { ChatHistoryRow } from "./ChatHistoryRow";
import { useChatHistory } from "./use-chat-history";
import type { ChatHistorySession } from "@/lib/apis/web/chat-history-api";
import type { ScenarioHostStyle } from "@/lib/scenario-client-style";
import { navigateToPromotedTestCase } from "@/components/chat-v2/shared/promote-to-eval-navigation";
import { ConvertChatSessionDialog } from "./convert-chat-session-dialog";

/** Delays (ms) after a turn completes to re-fetch list while backend ingestion may still be running. */
const HISTORY_REFETCH_RETRY_DELAYS_MS = [250, 800, 2000] as const;

interface ChatHistoryRailProps {
  activeSessionId?: string | null;
  /** Which host aesthetic to mimic for strong-highlight tokens (falls back to "claude"). */
  hostStyle?: ScenarioHostStyle;
  isAuthenticated: boolean;
  isStreaming: boolean;
  projectId?: string | null;
  requestHeaders?: HeadersInit;
  enabled?: boolean;
  refreshSignal?: number;
  onSelectThread: (session: ChatHistorySession) => void;
  /** Fired on row pointer-enter so callers can warm caches for the click path. */
  onPrefetchThread?: (session: ChatHistorySession) => void;
  onNewChat: (options?: { shared?: boolean }) => void;
  /** If the user has an active thread selected, run before archiving all (e.g. draft-confirm modal). */
  beforeResetChatAfterArchiveAll?: () => boolean | Promise<boolean>;
  /** After a successful archive-all, use this to clear the main chat if a history thread was active. */
  onArchiveAllComplete?: (hadActiveHistorySelection: boolean) => void;
  onSessionAction?: (event: {
    action:
      | "rename"
      | "archive"
      | "unarchive"
      | "share"
      | "unshare"
      | "pin"
      | "unpin";
    session: ChatHistorySession;
  }) => void | Promise<void>;
}

/**
 * One flat list of sessions. The fork split the rail into collapsible
 * "Sessions" / "Shared Sessions" folders because Convex served a personal and
 * a project-shared list; sphinx has exactly one operator, so the folder chrome
 * carried no information — every session lives in the root list, with the
 * archive-all / new-chat actions in a slim sticky row above it.
 */
export function ChatHistoryRail({
  activeSessionId,
  hostStyle = "claude",
  isAuthenticated,
  isStreaming,
  projectId,
  requestHeaders,
  enabled = true,
  refreshSignal = 0,
  onSelectThread,
  onPrefetchThread,
  onNewChat,
  beforeResetChatAfterArchiveAll,
  onArchiveAllComplete,
  onSessionAction,
}: ChatHistoryRailProps) {
  const [archiving, setArchiving] = useState(false);
  const [sessionToConvert, setSessionToConvert] =
    useState<ChatHistorySession | null>(null);
  const { personal, project, loading, error, isReactive, refetch, actions } =
    useChatHistory({
      projectId,
      enabled,
      requestHeaders,
    });

  // The seam serves everything as `personal` (project is always empty);
  // concatenated defensively so a row could never silently vanish.
  const sessions = useMemo(
    () => [...personal, ...project],
    [personal, project],
  );

  const wasStreamingRef = useRef(isStreaming);
  const didMountRefreshRef = useRef(false);

  useEffect(() => {
    const wasStreaming = wasStreamingRef.current;
    wasStreamingRef.current = isStreaming;

    const timeoutIds: number[] = [];

    if (wasStreaming && !isStreaming && !isReactive) {
      void refetch();
      for (const delayMs of HISTORY_REFETCH_RETRY_DELAYS_MS) {
        timeoutIds.push(
          window.setTimeout(() => {
            void refetch();
          }, delayMs),
        );
      }
    }

    return () => {
      for (const id of timeoutIds) {
        window.clearTimeout(id);
      }
    };
  }, [isStreaming, isReactive, refetch]);

  useEffect(() => {
    if (!enabled || isReactive) {
      return;
    }

    if (!didMountRefreshRef.current) {
      didMountRefreshRef.current = true;
      return;
    }

    void refetch();
  }, [enabled, isReactive, refetch, refreshSignal]);

  const canConvertToTestCase = Boolean(isAuthenticated);

  const handleArchiveAll = async () => {
    const canStart = sessions.length > 0 && !isStreaming && !archiving;
    if (!canStart) return;

    const activeInList = sessions.some((s) => s._id === activeSessionId);
    if (activeInList && beforeResetChatAfterArchiveAll) {
      const allowed = await Promise.resolve(beforeResetChatAfterArchiveAll());
      if (!allowed) {
        return;
      }
    }
    setArchiving(true);
    try {
      await actions.archiveManySessionIds(sessions.map((s) => s._id));
      onArchiveAllComplete?.(activeInList);
    } finally {
      setArchiving(false);
    }
  };

  return (
    <>
      <div className="flex h-full min-h-0 min-w-0 flex-col border-r">
        <ScrollArea
          className={cn(
            "min-h-0 min-w-0 flex-1",
            // Scrollbar: scoped polish — narrower, softer thumb, subtle hover expand.
            "[&_[data-slot=scroll-area-scrollbar]]:z-20",
            "[&_[data-slot=scroll-area-scrollbar]]:w-1.5",
            "[&_[data-slot=scroll-area-scrollbar]]:transition-[width,background-color]",
            "[&_[data-slot=scroll-area-scrollbar]]:duration-150",
            "hover:[&_[data-slot=scroll-area-scrollbar]]:w-2",
            "[&_[data-slot=scroll-area-thumb]]:bg-muted-foreground/30",
            "[&_[data-slot=scroll-area-thumb]]:transition-colors",
            "[&_[data-slot=scroll-area-thumb]]:duration-150",
            "hover:[&_[data-slot=scroll-area-thumb]]:bg-muted-foreground/60",
          )}
        >
          <div className="min-w-0 px-1 py-1">
            {loading && sessions.length === 0 && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}

            {!loading && error && sessions.length === 0 && (
              <div className="flex flex-col items-center gap-2 py-8 px-4 text-center">
                <p className="text-xs text-muted-foreground">
                  Could not load chat history.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-3 text-xs"
                  onClick={() => void refetch()}
                >
                  Retry
                </Button>
              </div>
            )}

            {enabled && !loading && !error && (
              <section aria-label="Sessions">
                <div
                  className={cn(
                    "sticky top-0 z-10 bg-background/95 supports-[backdrop-filter]:backdrop-blur-sm",
                    "flex w-full min-w-0 items-center justify-end gap-px px-1.5 py-1",
                  )}
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-5 no-underline [&_svg]:size-3"
                        aria-label="Archive all sessions"
                        onClick={() => void handleArchiveAll()}
                        disabled={
                          sessions.length === 0 || isStreaming || archiving
                        }
                      >
                        {archiving ? (
                          <Loader2 className="size-3 animate-spin" />
                        ) : (
                          <Archive className="size-3" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      Archive all sessions
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-5 no-underline [&_svg]:size-3"
                        aria-label="New chat"
                        onClick={() => onNewChat()}
                        disabled={isStreaming}
                      >
                        <Plus className="size-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">New chat</TooltipContent>
                  </Tooltip>
                </div>
                <div className="min-w-0 space-y-0.5 py-0.5 pl-1.5">
                  {sessions.map((session) => (
                    <ChatHistoryRow
                      key={session._id}
                      session={session}
                      isActive={session._id === activeSessionId}
                      isAuthenticated={isAuthenticated}
                      isStreaming={isStreaming}
                      sharedThreadsEnabled={false}
                      hostStyle={hostStyle}
                      onSelect={onSelectThread}
                      onPrefetch={onPrefetchThread}
                      onActionComplete={onSessionAction}
                      canConvertToTestCase={canConvertToTestCase}
                      onConvertToTestCase={setSessionToConvert}
                      actions={actions}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        </ScrollArea>
      </div>
      <ConvertChatSessionDialog
        open={sessionToConvert !== null}
        session={sessionToConvert}
        isAuthenticated={isAuthenticated}
        projectId={projectId ?? null}
        requestHeaders={requestHeaders}
        onOpenChange={(open) => {
          if (!open) {
            setSessionToConvert(null);
          }
        }}
        onImported={({ suiteId, testCaseId }) => {
          setSessionToConvert(null);
          navigateToPromotedTestCase({ suiteId, testCaseId });
        }}
      />
    </>
  );
}
